import { createContext, useMemo, useState } from 'react';
import { useOperationHook } from '../../../../../../hooks/useOperationHook';
import { useWaybillResolver } from '../../../../../../hooks/useWaybillResolver';
import { IManifestQuote } from '../../../../../../model/EnquiryModal';
import { IAgreementODAPrice } from '../../../../../../model/OperationModel';
import { useOperationStore } from '../../../../../../stores/OperationStore';
import { ACCOUNT_ID, CASH_ID, INTERNATIONAL, PRODUCT_TYPE } from '../../../../../../utils/app-common';

type Props = {
	children: JSX.Element,
	readOnly?: boolean,
}
type IManifestContext = {
	readOnly?: boolean,
	waybillNo?: string,
	id?: number,
	manifestId?: number,
	billTypeId?: number,
	clientId?: number,
	destOdaCharge?: string | number | undefined,
	originOdaCharge?: string | number | undefined,
	isPickedup?: boolean,
	isDisabled?: boolean
	isInvoiced?: boolean,
	isManifestorConfirmed?: boolean,
	accountId?: string,
	isDelivered?: boolean,
	isOracleInvoiced?: boolean,
	isInternational?: boolean,
	isInternationalRangeProduct?: boolean,
	sentForOracleInvoicing?: boolean,
	deliveryId?: number,
	pieceCount?: number | string,
	agreementBillPeriod?: number | string,
	productId?: number,
	lastStatus?: string,
	isAuditOpen?: boolean,
	isTrackingOpen?: boolean,
	confirmedByFinanceId?: boolean,
	fromOperation?: boolean,
	confirmedBySalespersonId?: boolean,
	manifestQuote?: IManifestQuote,
	isConfirmationModal: boolean,
	agreementOdaPriceList?: IAgreementODAPrice[],
	modifiedByEmp?: string,
	manifestedUpdatedDate?: number,
	confirmedBySalesperson?: string,
	salespersonConfirmedDate?: number,
	isSalesmanApproved?: boolean,
	isSalesmanRejected?: boolean,
	rejectedBySalesmanName?: string,
	salesmanRejectionDate?: number,
	salesmanRejectionRemarks?: string,
	confirmedByFinance?: string,
	financeConfirmedDate?: number,
	rejectedByFinance?: string,
	financeRejectionDate?: number,
	isFinanceApproved?: boolean,
	isFinanceRejected?: boolean,
	financeRejectionRemarks?: string,
	isInvoicedManifest?: boolean,
	product?: string,
	setIsAuditOpen: (value: React.SetStateAction<boolean>) => void,
	setIsTrackingOpen: (value: React.SetStateAction<boolean>) => void
	setIsConfirmationModal: (value: React.SetStateAction<boolean>) => void
	consigneeUpdated?: boolean
	manifestOnHold?: boolean
}
const ManifestContext = createContext({} as IManifestContext);

const ManifestContextComponent = ({ children, readOnly = false }: Props) => {

	const { billTypeId, isPickedup, id, isInvoiced, isDelivered, lastStatus, productName, fromOperation } = useOperationHook() || {};
	const waybillNo = useWaybillResolver();
	const isDisabled = ((billTypeId === ACCOUNT_ID) || (billTypeId === CASH_ID)) ? (isInvoiced) : false;
	const manifestationList = useOperationStore((state) => state?.manifestationList);
	const { product, id: manifestId, pieceCount, confirmedByFinanceId, confirmedBySalespersonId, manifestQuote, clientId, accountId, isOracleInvoiced, sentForOracleInvoicing,
		productId, agreementBillPeriod, manifetDetail, agreementOdaPriceList, deliveryId, modifiedByEmp, manifestedUpdatedDate, confirmedBySalesperson, salespersonConfirmedDate,
		isSalesmanApproved, isSalesmanRejected, rejectedBySalesmanName, salesmanRejectionDate, salesmanRejectionRemarks, confirmedByFinance, financeConfirmedDate,
		isFinanceApproved, isFinanceRejected, rejectedByFinance, financeRejectionDate, financeRejectionRemarks, isInvoiced: isInvoicedManifest, consigneeUpdated, manifestOnHold } = manifestationList || {};
	const [isAuditOpen, setIsAuditOpen] = useState(false);
	const [isTrackingOpen, setIsTrackingOpen] = useState(false);
	const [isConfirmationModal, setIsConfirmationModal] = useState<boolean>(false);
	const isInternational = product?.toLocaleLowerCase()?.startsWith(INTERNATIONAL);
	const isInternationalRangeProduct = !!(product && [PRODUCT_TYPE.INTERNATIONAL_ROAD_EXPRESS, PRODUCT_TYPE.INTERNATIONAL_ROAD_PALLETS].includes(product as PRODUCT_TYPE));
	const values = useMemo(() => ({
		readOnly,
		waybillNo,
		id,
		billTypeId,
		isPickedup,
		isDisabled,
		isInvoiced,
		isDelivered,
		lastStatus,
		isInternational,
		isInternationalRangeProduct,
		setIsAuditOpen,
		isAuditOpen,
		setIsTrackingOpen,
		isTrackingOpen,
		setIsConfirmationModal,
		manifestId,
		pieceCount: pieceCount,
		confirmedByFinanceId: Boolean(confirmedByFinanceId),
		confirmedBySalespersonId: Boolean(confirmedBySalespersonId),
		manifestQuote: {
			manifestId,
			enquiryId: manifestQuote?.enquiryQuotationId,
			enquiryQuotationId: manifestQuote?.enquiryQuotationId,
			quotationNumber: manifestQuote?.quotationNumber
		},
		clientId: clientId,
		productId: productId,
		agreementBillPeriod: agreementBillPeriod,
		originOdaCharge: manifetDetail?.originOdaCharge,
		destOdaCharge: manifetDetail?.destOdaCharge,
		agreementOdaPriceList: agreementOdaPriceList,
		isConfirmationModal,
		isManifestorConfirmed: isInvoiced,
		accountId: accountId,
		isOracleInvoiced: isOracleInvoiced,
		sentForOracleInvoicing: sentForOracleInvoicing,
		deliveryId: deliveryId,
		fromOperation,
		modifiedByEmp,
		manifestedUpdatedDate,
		confirmedBySalesperson,
		salespersonConfirmedDate,
		isSalesmanApproved,
		isSalesmanRejected,
		rejectedBySalesmanName,
		salesmanRejectionDate,
		salesmanRejectionRemarks,
		confirmedByFinance,
		financeConfirmedDate,
		isFinanceApproved,
		isFinanceRejected,
		rejectedByFinance,
		financeRejectionDate,
		financeRejectionRemarks,
		product,
		isInvoicedManifest,
		consigneeUpdated,
		manifestOnHold
	}), [readOnly, waybillNo, id, billTypeId, isPickedup, isDisabled, isInvoiced, isDelivered, lastStatus, isInternational, isInternationalRangeProduct, isAuditOpen, isTrackingOpen, manifestId, pieceCount, confirmedByFinanceId, confirmedBySalespersonId, manifestQuote?.enquiryQuotationId, manifestQuote?.quotationNumber, clientId, productId, agreementBillPeriod, manifetDetail?.originOdaCharge, manifetDetail?.destOdaCharge, agreementOdaPriceList, isConfirmationModal, accountId, isOracleInvoiced, sentForOracleInvoicing, deliveryId, fromOperation, modifiedByEmp, manifestedUpdatedDate, confirmedBySalesperson, salespersonConfirmedDate, isSalesmanApproved, isSalesmanRejected, rejectedBySalesmanName, salesmanRejectionDate, salesmanRejectionRemarks, confirmedByFinance, financeConfirmedDate, isFinanceApproved, isFinanceRejected, rejectedByFinance, financeRejectionDate, financeRejectionRemarks, product, isInvoicedManifest, consigneeUpdated, manifestOnHold])


	return (
		<ManifestContext.Provider value={values}>
			{children}
		</ManifestContext.Provider>
	)
}

export { ManifestContext };
export default ManifestContextComponent